import { DocStatusEnum, TDocType, TDocument } from '../types.ts';
import { useEffect, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import useGetContragentTypes from '../../../hooks/useGetContragentTypes.ts';
import { getOwnerFullName, getOwnerIdForDoc, mapDocName } from '../utils.ts';
import { DOCUMENT_TOOLTIPS, WEB_DOCS, WebDocName } from '~/constants/docs.ts';
import { useDocumentsQuery } from '~/queries/useDocumentsQuery.ts';
import { useSelector } from 'react-redux';
import { selectControllingPersons, selectOwners } from '~/store/identification-form/selectors.ts';
import { TControllingPersons } from '~/modules/identification/forms/TaxResidencyAdditionalInformation/types.ts';

export const useGetDocuments = (
  documentIds: number[],
  storingDocuments: TDocument[],
  informers: Partial<Record<WebDocName, boolean>> = {},
  applicationId?: number | string
) => {
  const { webDocs = [], personalDocs = [], isLoading } = useDocumentsQuery();
  const docTypes = useMemo(() => [...personalDocs, ...webDocs], [webDocs, personalDocs]);

  const { isLegalResident } = useGetContragentTypes();

  const owners = useSelector(selectOwners);
  const beneficiaryOwners = useMemo(
    () => (owners || []).filter((owner) => owner.isBeneficiary),
    [owners]
  );

  const controllingPersons = useSelector(selectControllingPersons);
  const isTaxResidentOtherJurisdictionControllingPersons = useMemo(() => {
    if (!controllingPersons) return [];

    return controllingPersons.filter(
      (controllingPerson: TControllingPersons) => controllingPerson.isTaxResidentOtherJurisdiction
    );
  }, [controllingPersons]);

  const [documents, setDocuments] = useState<TDocument[]>([]);
  const informersKey = useMemo(() => JSON.stringify(informers), [informers]);

  const getWebDocName = (idDictWebDocument: number) =>
    Object.entries(WEB_DOCS).find(([, value]) => value === idDictWebDocument)?.[0] as
      WebDocName | undefined;

  const getTooltipText = (idDictWebDocument: number) => {
    const webDocName = getWebDocName(idDictWebDocument);
    return webDocName ? DOCUMENT_TOOLTIPS[webDocName] : undefined;
  };

  const getShowInformer = (idDictWebDocument: number) => {
    const webDocName = getWebDocName(idDictWebDocument);
    return webDocName ? Boolean(informers[webDocName]) : false;
  };

  const uboDocIds = [
    WEB_DOCS[WebDocName.uboPassport],
    WEB_DOCS[WebDocName.uboTinCode],
    WEB_DOCS[WebDocName.crsControllingPersonTaxResidencyAssessment],
  ];

  const withOwnerName = (name: string, label: string | null) =>
    label ? `${name} (${label})` : name;

  const mapDocumentsWithNames = () => {
    const filteredDocTypes: TDocType[] = docTypes.filter((doc: TDocType) =>
      documentIds.includes(doc.id)
    );

    const groupedDocs: { [key: number]: TDocument[] } = {};

    storingDocuments.forEach((doc) => {
      if (!groupedDocs[doc.idDictWebDocument]) {
        groupedDocs[doc.idDictWebDocument] = [];
      }
      groupedDocs[doc.idDictWebDocument]?.push(doc);
    });

    const resolvedOwnerIdByDocKey = new Map<string, string>();

    uboDocIds.forEach((docTypeId) => {
      (groupedDocs[docTypeId] || []).forEach((doc) => {
        if (doc.idDoc && applicationId != null) {
          const resolvedOwnerId = getOwnerIdForDoc(applicationId, docTypeId, doc.idDoc);
          if (resolvedOwnerId) {
            resolvedOwnerIdByDocKey.set(`${docTypeId}:${doc.id}`, resolvedOwnerId);
          }
        }
      });
    });

    const usedOwnerIdsByType = new Map<number, Set<string>>();
    uboDocIds.forEach((docTypeId) => {
      usedOwnerIdsByType.set(
        docTypeId,
        new Set(
          Array.from(resolvedOwnerIdByDocKey.entries())
            .filter(([key]) => key.startsWith(`${docTypeId}:`))
            .map(([, ownerId]) => ownerId)
        )
      );
    });

    const freeOwnerQueueByType = new Map<number, typeof beneficiaryOwners>();
    uboDocIds.forEach((docTypeId) => {
      const usedOwnerIds = usedOwnerIdsByType.get(docTypeId) ?? new Set<string>();
      freeOwnerQueueByType.set(
        docTypeId,
        beneficiaryOwners.filter((owner) => !usedOwnerIds.has(owner.id))
      );
    });

    const freeControllingPersonsQueue = [...isTaxResidentOtherJurisdictionControllingPersons];
    const isCrsControllingPersonDocument = (idDictWebDocument: number) =>
      idDictWebDocument === WEB_DOCS[WebDocName.crsControllingPersonTaxResidencyAssessment];
    const getControllingPersonFullName = (person: TControllingPersons) =>
      [person.lastName, person.firstName].filter(Boolean).join(' ');

    const resolveOwnerForDoc = (
      idDictWebDocument: number,
      docKey: string | undefined
    ): { label: string | null; ownerId?: string } => {
      if (!uboDocIds.includes(idDictWebDocument)) {
        return { label: null };
      }
      if (isCrsControllingPersonDocument(idDictWebDocument)) {
        const resolvedOwnerId = docKey ? resolvedOwnerIdByDocKey.get(docKey) : null;
        if (resolvedOwnerId) {
          const controllingPerson = isTaxResidentOtherJurisdictionControllingPersons.find(
            (person) => person.id === resolvedOwnerId
          );
          return {
            label: controllingPerson ? getControllingPersonFullName(controllingPerson) : null,
            ownerId: resolvedOwnerId,
          };
        }
        const controllingPerson = freeControllingPersonsQueue.shift();
        return {
          label: controllingPerson ? getControllingPersonFullName(controllingPerson) : null,
        };
      }

      const resolvedOwnerId = docKey ? resolvedOwnerIdByDocKey.get(docKey) : null;

      if (resolvedOwnerId) {
        const owner = beneficiaryOwners.find((o) => o.id === resolvedOwnerId);
        return {
          label: owner ? getOwnerFullName(owner) : null,
          ownerId: resolvedOwnerId,
        };
      }
      const owner = freeOwnerQueueByType.get(idDictWebDocument)?.shift();
      return {
        label: owner ? getOwnerFullName(owner) : null,
        ownerId: owner?.id,
      };
    };

    const remainingGroupedDocs: {
      [key: number]: TDocument[];
    } = Object.fromEntries(Object.entries(groupedDocs).map(([key, docs]) => [key, [...docs]]));
    const documents = documentIds?.map((id) => {
      const docType = filteredDocTypes.find((docType) => docType.id === id);
      const realDoc = remainingGroupedDocs[id]?.shift();
      if (realDoc) {
        const { label, ownerId } = resolveOwnerForDoc(id, `${id}:${realDoc.id}`);
        return {
          ...realDoc,
          nameDoc: withOwnerName(mapDocName(isLegalResident, docType, realDoc), label),
          documentName: realDoc.nameDoc,
          id: uuidv4(),
          ownerId,
          tooltipText: getTooltipText(id),
          showInformer: getShowInformer(id),
        };
      }

      const { label, ownerId } = resolveOwnerForDoc(id, undefined);
      return {
        idDictWebDocument: id,
        nameDoc: withOwnerName(mapDocName(isLegalResident, docType), label),
        idDoc: 0,
        docStatus: [DocStatusEnum.NOT_VERIFIED],
        id: uuidv4(),
        ownerId,
        tooltipText: getTooltipText(id),
        showInformer: getShowInformer(id),
      } as TDocument;
    });

    return documents;
  };

  useEffect(() => {
    !isLoading && setDocuments(mapDocumentsWithNames());
  }, [
    documentIds,
    storingDocuments,
    docTypes,
    informersKey,
    isLoading,
    beneficiaryOwners,
    controllingPersons,
    applicationId,
  ]);

  return { documents };
};
